import { MapView, ReactLeaflet } from '@elotech/components';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import { Leaflet } from '@elotech/components';
import {
  corPorSituacao,
  SITUACAO_INTERVENCAO_OXY
} from './situacaoIntervencaoOxy';

const { CircleMarker, Popup } = ReactLeaflet;

const formatarPercentual = value =>
  value ? `${Number(value).toFixed(2)}%` : '-';

const formatarData = value => {
  if (!value) return '-';
  const [ano, mes, dia] = value.toString().split('-');
  return dia ? `${dia}/${mes}/${ano}` : value;
};

const formatarMoeda = value => {
  if (value == null) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

export const MapaObrasPublicasMapView = forwardRef(
  ({ obras, loading, height = '100%', onMarkerClick, onVerObra }, ref) => {
    const mapRef = useRef(null);
    const circleRefs = useRef({});

    useEffect(() => {
      const pontos = obras
        .flatMap(o => o.imoveis)
        .filter(im => im.latitude && im.longitude)
        .map(im => new Leaflet.LatLng(im.latitude, im.longitude));
      if (pontos.length > 0 && mapRef.current) {
        const bounds = new Leaflet.LatLngBounds(pontos);
        mapRef.current.leafletElement.fitBounds(bounds, { padding: [40, 40] });
      }
    }, [obras]);

    useImperativeHandle(ref, () => ({
      flyToImovel: (obra, imIdx) => {
        const imovel = obra.imoveis[imIdx];
        if (!imovel.latitude || !imovel.longitude) return;
        mapRef.current.leafletElement.flyTo(
          new Leaflet.LatLng(imovel.latitude, imovel.longitude),
          16
        );
        const key = `${obra.idOxy}-${imIdx}`;
        circleRefs.current[key] &&
          circleRefs.current[key].leafletElement &&
          circleRefs.current[key].leafletElement.openPopup();
      }
    }));

    return (
      <MapView ref={mapRef} loading={loading} height={height}>
        {obras.flatMap(obra =>
          obra.imoveis
            .filter(im => im.latitude && im.longitude)
            .map((imovel, imIdx) => {
              const key = `${obra.idOxy}-${imIdx}`;
              const cor = corPorSituacao(obra.situacao);
              const situacaoLabel =
                SITUACAO_INTERVENCAO_OXY[obra.situacao] || obra.situacao;
              return (
                <CircleMarker
                  key={key}
                  ref={r => {
                    if (r) circleRefs.current[key] = r;
                    else delete circleRefs.current[key];
                  }}
                  center={[imovel.latitude, imovel.longitude]}
                  radius={6}
                  color={cor}
                  fillColor={cor}
                  fillOpacity={0.85}
                  onClick={e => {
                    e.originalEvent && e.originalEvent.stopPropagation();
                    onMarkerClick && onMarkerClick(obra);
                  }}
                >
                  <Popup>
                    <div style={{ fontSize: '12px', minWidth: '200px' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          borderBottom: '1px solid #eee',
                          paddingBottom: '4px',
                          marginBottom: '6px'
                        }}
                      >
                        <strong style={{ flex: 1 }}>
                          Nº {obra.numero}
                          {obra.codigoTCE ? ` | TCE: ${obra.codigoTCE}` : ''}
                        </strong>
                        <span
                          style={{
                            background: cor,
                            color: '#fff',
                            borderRadius: '4px',
                            padding: '1px 6px',
                            fontSize: '11px'
                          }}
                        >
                          {situacaoLabel}
                        </span>
                      </div>
                      {imovel.descricaoBem && (
                        <p
                          style={{
                            margin: '0 0 4px',
                            fontWeight: 'bold',
                            fontSize: '11px'
                          }}
                        >
                          {imovel.descricaoBem}
                        </p>
                      )}
                      <p style={{ margin: '0 0 6px' }}>{obra.objeto}</p>
                      {imovel.enderecoCompleto && (
                        <p
                          style={{
                            margin: '0 0 6px',
                            color: '#666',
                            fontSize: '11px'
                          }}
                        >
                          {imovel.enderecoCompleto}
                        </p>
                      )}
                      {onVerObra && (
                        <button
                          type="button"
                          className="btn btn-link"
                          style={{ padding: '2px 0', fontSize: '12px' }}
                          onClick={() => onVerObra(obra.idOxy)}
                        >
                          Abrir Intervenção
                        </button>
                      )}
                      <table
                        style={{
                          width: '100%',
                          fontSize: '11px',
                          borderCollapse: 'collapse'
                        }}
                      >
                        <tbody>
                          {obra.situacao !== 'NAO_INICIADA' && (
                            <tr>
                              <td>Conclusão:</td>
                              <td>
                                <strong>
                                  {formatarPercentual(obra.percentual)}
                                </strong>
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td>Valor:</td>
                            <td>
                              <strong>{formatarMoeda(obra.valor)}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td>Ano:</td>
                            <td>
                              <strong>{obra.anoIntervencao ?? '-'}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td>Data base:</td>
                            <td>
                              <strong>{formatarData(obra.dataBase)}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td>Início:</td>
                            <td>
                              <strong>{formatarData(obra.dataInicio)}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td>Prev. término:</td>
                            <td>
                              <strong>
                                {formatarData(obra.dataPrevisaoTermino)}
                              </strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })
        )}
      </MapView>
    );
  }
);

MapaObrasPublicasMapView.displayName = 'MapaObrasPublicasMapView';
